(() => {
  const configNode = document.getElementById('galea-punt-quantity-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const scopedIds = new Set(config.productIds.map(String));
  const minimum = config.minimum;
  const formStates = new WeakMap();
  const approvedForms = new WeakSet();
  const approvedCheckoutClicks = new WeakSet();
  let pendingChange = null;
  let checkingCheckout = false;
  let transferCheckout = null;

  const formFor = element => element && element.closest('form');
  const quantityValue = form => Number(form.querySelector('[name="quantity"]')?.value);
  const validQuantity = quantity => Number.isSafeInteger(quantity) && quantity > 0;
  const mailLink = (subject, body) => `mailto:info@galeahome.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const groupKey = item => JSON.stringify([String(item.variant_id || item.variant?.id || item.id), item.properties === undefined ? null : item.properties, String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  const configurationText = item => [item.variant_title || item.variant?.title || '',
    ...Object.entries(item.properties || {}).filter(([key, value]) => !key.startsWith('_') && value !== '' && value !== null).map(([key, value]) => `${key}: ${value}`),
    item.selling_plan_allocation?.selling_plan?.name || ''].filter(Boolean).join('; ');
  const groupItems = items => {
    const groups = new Map();
    items.forEach(item => {
      const key = groupKey(item);
      const existing = groups.get(key);
      if (existing) existing.quantity += Number(item.quantity);
      else groups.set(key, {...item, quantity: Number(item.quantity)});
    });
    return groups;
  };
  const invalidGroups = items => [...groupItems(items).values()].filter(item => scopedIds.has(String(item.product_id || item.product?.id)) && item.quantity > 0 && item.quantity < minimum);
  const cartForms = () => [...document.querySelectorAll('form.Cart')];
  const cartData = form => {
    const node = form.querySelector('[data-galea-punt-cart-json]');
    if (!node) return {items: []};
    try { return JSON.parse(node.textContent); } catch { return {items: []}; }
  };
  const hasScopedCart = () => cartForms().some(form => form.querySelector('[data-galea-punt-cart-guard]')?.dataset.hasPunt === 'true');

  function productState(form) {
    if (!form) return null;
    let state = formStates.get(form);
    if (state) return state;
    const control = form.querySelector('[data-galea-punt-product]');
    if (!control) return null;
    const section = form.closest('[data-section-type]') || form.parentElement;
    let product = null;
    try { product = JSON.parse(section.querySelector('[data-product-json]').textContent).product; } catch {}
    const selected = String(form.querySelector('[name="id"]')?.value || '');
    const variant = product?.variants?.find(item => String(item.id) === selected) || null;
    state = {control, variant, product};
    formStates.set(form, state);
    return state;
  }

  function updateProduct(form, variant, variantProvided = false) {
    const state = productState(form);
    if (!state) return;
    if (variantProvided) state.variant = variant;
    const quantity = quantityValue(form);
    const isValid = validQuantity(quantity);
    const canBuy = isValid && quantity >= minimum && state.variant?.available === true;
    const quote = state.control.querySelector('[data-galea-punt-quote]');
    const status = state.control.querySelector('[data-galea-punt-status]');
    const buy = form.querySelector('[data-galea-punt-buy]');
    const button = buy?.querySelector('.ProductForm__AddToCart');
    const input = form.querySelector('[name="quantity"]');
    input.setCustomValidity(isValid ? '' : config.invalidQuantity);
    if (buy) buy.hidden = !canBuy;
    if (button) {
      button.disabled = !canBuy;
      if (canBuy) button.classList.replace('Button--secondary', 'Button--primary');
      const separator = button.querySelector('.Button__SeparatorDot');
      const buttonPrice = button.querySelector('[data-money-convertible]');
      if (separator) separator.hidden = true;
      if (buttonPrice) buttonPrice.hidden = true;
    }
    quote.hidden = canBuy;
    quote.setAttribute('aria-disabled', isValid ? 'false' : 'true');
    status.textContent = !isValid ? config.invalidQuantity : canBuy ? config.buyStatus : quantity >= minimum ? config.unavailable : config.quoteStatus;
    const variantUrl = new URL(state.control.dataset.productUrl, window.location.origin);
    if (state.variant) variantUrl.searchParams.set('variant', state.variant.id);
    const body = [config.mailIntro, state.control.dataset.productTitle,
      `${config.configuration}: ${configurationText({variant_title: state.variant?.title, properties: Object.fromEntries([...new FormData(form)].filter(([key]) => /^properties\[[^_]/.test(key)).map(([key, value]) => [key.slice(11, -1), value]))})}`,
      `${config.quantity}: ${isValid ? quantity : ''}`, variantUrl.href,
      `${config.postcode}:`].join('\n');
    quote.href = mailLink(config.mailSubject.replace('__PRODUCT__', state.control.dataset.productTitle), body);
  }

  function setCartControls(form) {
    const guard = form.querySelector('[data-galea-punt-cart-guard]');
    const blocked = guard?.dataset.blocked === 'true';
    const busy = Boolean(pendingChange) || checkingCheckout || Boolean(transferCheckout);
    const anyBlocked = cartForms().some(cart => cart.querySelector('[data-galea-punt-cart-guard]')?.dataset.blocked === 'true');
    document.body.toggleAttribute('data-galea-punt-checkout-blocked', busy || anyBlocked);
    form.querySelectorAll('[name="checkout"]').forEach(button => {
      button.disabled = Boolean(blocked || busy);
      button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
    });
    form.querySelectorAll('.QuantitySelector__CurrentQuantity').forEach(input => { input.disabled = busy; });
    form.querySelectorAll('[data-action="update-item-quantity"], [data-action="remove-item"]').forEach(link => {
      if (busy) link.setAttribute('aria-disabled', 'true');
      else link.removeAttribute('aria-disabled');
    });
  }

  function showCartMessage(message, groups = null) {
    cartForms().forEach(form => {
      const guard = form.querySelector('[data-galea-punt-cart-guard]');
      if (!guard) return;
      const panel = guard.querySelector('[data-galea-punt-cart-message]');
      if (!panel) return;
      panel.hidden = false;
      let status = panel.querySelector('[data-galea-punt-cart-status]');
      if (!status) {
        status = document.createElement('p');
        status.dataset.galeaPuntCartStatus = '';
        panel.prepend(status);
      }
      status.textContent = message;
      if (message === config.cartError && !panel.querySelector('[data-galea-punt-cart-reload]')) {
        const reload = document.createElement('a');
        reload.dataset.galeaPuntCartReload = '';
        reload.className = 'Link Link--underline';
        reload.href = config.cartUrl;
        reload.textContent = config.cartReload;
        panel.append(reload);
      }
      if (groups) {
        guard.dataset.blocked = groups.length ? 'true' : 'false';
        const list = panel.querySelector('[data-galea-punt-cart-list]');
        if (list) {
          list.replaceChildren();
          groups.forEach(item => {
            const li = document.createElement('li');
            li.textContent = `${item.product_title || item.title || ''} · ${configurationText(item)} · ${config.quantity}: ${item.quantity}`;
            list.append(li);
          });
        }
        const quote = panel.querySelector('[data-galea-punt-cart-quote]');
        if (quote) {
          const lines = groups.map(item => {
            const url = new URL(item.url || config.cartUrl, window.location.origin);
            return [item.product_title || item.title || '', `${config.configuration}: ${configurationText(item)}`, `${config.quantity}: ${item.quantity}`, url.href].join('\n');
          });
          quote.href = mailLink(config.cartQuoteSubject, [config.mailIntro, ...lines, `${config.postcode}:`].join('\n\n'));
          quote.hidden = !groups.length;
        }
      }
      panel.setAttribute('tabindex', '-1');
      setCartControls(form);
    });
  }

  function initializeCart(form) {
    const data = cartData(form);
    reconcileCart(data, form);
    setCartControls(form);
  }

  function reconcileCart(data, form) {
    const itemCount = data.items.reduce((total, item) => total + Number(item.quantity), 0);
    document.querySelectorAll('.Header__CartCount').forEach(node => { node.textContent = String(itemCount); });
    document.querySelectorAll('.Header__CartDot').forEach(node => node.classList.toggle('is-visible', itemCount > 0));
    if (pendingChange) {
      const actual = groupItems(data.items).get(pendingChange.group)?.quantity || 0;
      if (actual === pendingChange.expectedQuantity) {
        clearTimeout(pendingChange.timer);
        pendingChange = null;
        cartForms().forEach(setCartControls);
        refreshOtherCarts(form);
      }
    }
  }

  async function refreshOtherCarts(source) {
    for (const other of cartForms().filter(form => form !== source)) {
      const view = other.closest('#sidebar-cart') ? 'drawer' : 'ajax';
      try {
        const response = await fetch(`${config.cartUrl}?view=${view}&timestamp=${Date.now()}`, {credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) continue;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('form.Cart');
        if (fresh && other.isConnected) other.replaceWith(fresh);
      } catch {}
    }
  }

  function startCartChange(form, target, event) {
    if (!hasScopedCart()) return;
    if (pendingChange || checkingCheckout || transferCheckout) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const data = cartData(form);
    const key = target.getAttribute('data-line-id');
    const line = data.items.find(item => item.key === key);
    if (!line) return;
    const isInput = target.matches('input');
    const quantity = isInput ? Number(target.value) : Number(target.dataset.quantity);
    if (isInput && !validQuantity(quantity)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      target.setCustomValidity(config.invalidQuantity);
      target.reportValidity();
      return;
    }
    if (!Number.isSafeInteger(quantity) || quantity < 0) return;
    if (isInput) target.setCustomValidity('');
    const group = groupKey(line);
    const expectedQuantity = groupItems(data.items).get(group).quantity - line.quantity + quantity;
    pendingChange = {group, expectedQuantity, timer: setTimeout(() => showCartMessage(config.cartError), 20000)};
    cartForms().forEach(setCartControls);
  }

  async function checkCheckout(form, submitter, transfer = null, replayClick = false) {
    if (!form) return;
    if (pendingChange) {
      showCartMessage(config.cartPending);
      return;
    }
    if (checkingCheckout || transferCheckout || !form.reportValidity()) return;
    checkingCheckout = true;
    cartForms().forEach(setCartControls);
    try {
      const response = await fetch(`${config.cartUrl}.js`, {credentials: 'same-origin', cache: 'no-store', headers: {'Accept': 'application/json'}});
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      const invalid = invalidGroups(cart.items);
      if (invalid.length) {
        checkingCheckout = false;
        showCartMessage(config.cartNotice, invalid);
        form.querySelector('[data-galea-punt-cart-message]')?.focus();
        return;
      }
      checkingCheckout = false;
      cartForms().forEach(other => {
        const guard = other.querySelector('[data-galea-punt-cart-guard]');
        if (guard) guard.dataset.blocked = 'false';
        setCartControls(other);
      });
      const button = transfer ? transfer.button : submitter?.isConnected && submitter.form === form ? submitter : form.querySelector('[name="checkout"]');
      if (!button || !form.isConnected) return;
      if (transfer && (!transfer.host.isConnected || !button.isConnected || !transfer.host.shadowRoot?.contains(button))) return;
      const quantityUpdates = [...form.querySelectorAll('[name="updates[]"]')];
      quantityUpdates.forEach(input => { input.disabled = true; });
      approvedForms.add(form);
      try {
        if (transfer) {
          approvedCheckoutClicks.add(button);
          button.click();
          if (transfer.host.hasAttribute('loading')) {
            transferCheckout = {
              host: transfer.host,
              timer: setTimeout(() => showCartMessage(config.cartError), 20000)
            };
            cartForms().forEach(setCartControls);
          }
        } else if (replayClick) {
          approvedCheckoutClicks.add(button);
          button.click();
        } else {
          form.requestSubmit(button);
        }
      } finally {
        approvedForms.delete(form);
        approvedCheckoutClicks.delete(button);
        setTimeout(() => quantityUpdates.forEach(input => {
          if (input.isConnected) input.disabled = Boolean(pendingChange) || checkingCheckout || Boolean(transferCheckout);
        }), 0);
      }
    } catch {
      checkingCheckout = false;
      showCartMessage(config.cartError);
      cartForms().forEach(setCartControls);
    }
  }

  document.addEventListener('click', event => {
    const path = event.composedPath();
    const transferHost = path.find(node => node.matches?.('scala-cart-button'));
    if (transferHost && hasScopedCart()) {
      const transferButton = path.find(node => node.matches?.('button[data-trigger]'));
      if (transferButton && approvedCheckoutClicks.has(transferButton)) {
        approvedCheckoutClicks.delete(transferButton);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const form = transferHost.closest('form.Cart') || cartForms()[0];
      if (transferButton) checkCheckout(form, null, {host: transferHost, button: transferButton});
      return;
    }
    const step = event.target.closest('[data-galea-punt-step]');
    if (step) {
      const form = formFor(step);
      const input = form.querySelector('[name="quantity"]');
      const current = quantityValue(form);
      input.value = String(Math.max(1, (validQuantity(current) ? current : 1) + Number(step.dataset.galeaPuntStep)));
      updateProduct(form);
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const quote = event.target.closest('[data-galea-punt-quote]');
    if (quote) {
      const form = formFor(quote);
      updateProduct(form);
      if (!validQuantity(quantityValue(form))) event.preventDefault();
      return;
    }
    const add = event.target.closest('.ProductForm__AddToCart');
    if (add) {
      const form = formFor(add);
      const state = productState(form);
      if (state) {
        const quantity = quantityValue(form);
        if (!validQuantity(quantity) || quantity < minimum || !state.variant?.available) {
          event.preventDefault();
          event.stopImmediatePropagation();
          updateProduct(form);
          return;
        }
      }
    }
    const cartAction = event.target.closest('[data-action="update-item-quantity"], [data-action="remove-item"]');
    if (cartAction?.closest('form.Cart')) startCartChange(formFor(cartAction), cartAction, event);
    const checkout = event.target.closest('[name="checkout"]');
    if (checkout?.closest('form.Cart') && hasScopedCart()) {
      if (approvedCheckoutClicks.has(checkout)) {
        approvedCheckoutClicks.delete(checkout);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      checkCheckout(formFor(checkout), checkout, null, true);
    }
  }, true);

  document.addEventListener('input', event => {
    const form = formFor(event.target);
    if (event.target.matches('[name="quantity"]') && form?.querySelector('[data-galea-punt-product]')) updateProduct(form);
  }, true);

  document.addEventListener('change', event => {
    const form = formFor(event.target);
    if (event.target.matches('[name="quantity"]') && form?.querySelector('[data-galea-punt-product]')) {
      event.stopImmediatePropagation();
      updateProduct(form);
    } else if (event.target.matches('.QuantitySelector__CurrentQuantity') && form?.matches('.Cart')) {
      startCartChange(form, event.target, event);
    }
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (form.querySelector('[data-galea-punt-product]')) {
      const state = productState(form);
      const quantity = quantityValue(form);
      if (!validQuantity(quantity) || quantity < minimum || !state.variant?.available) {
        event.preventDefault();
        event.stopImmediatePropagation();
        updateProduct(form);
      }
    } else if (form.matches('.Cart') && hasScopedCart()) {
      if (approvedForms.has(form)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      checkCheckout(form, event.submitter);
    }
  }, true);

  document.addEventListener('variant:changed', event => {
    event.target.querySelectorAll('form').forEach(form => {
      if (form.querySelector('[data-galea-punt-product]')) updateProduct(form, event.detail?.variant || null, true);
    });
  });

  function initialize(root) {
    const forms = [];
    if (root.matches?.('form')) forms.push(root);
    root.querySelectorAll?.('form').forEach(form => forms.push(form));
    forms.forEach(form => {
      if (form.querySelector('[data-galea-punt-product]')) updateProduct(form);
      if (form.matches('.Cart')) initializeCart(form);
    });
    root.querySelectorAll?.('[data-galea-punt-cart-json]').forEach(node => {
      if (!node.closest('form.Cart')) {
        try { reconcileCart(JSON.parse(node.textContent)); } catch {}
      }
    });
  }

  initialize(document);
  new MutationObserver(records => {
    if (transferCheckout && !transferCheckout.host.hasAttribute('loading')) {
      clearTimeout(transferCheckout.timer);
      transferCheckout = null;
      cartForms().forEach(setCartControls);
    }
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1 && (node.matches('form') || node.querySelector('form, [data-galea-punt-cart-json]'))) initialize(node);
    }));
  }).observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['loading']});
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
